import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  DRESS_CODE_LABELS,
  GuestRequest,
  VIBE_TAG_LABELS,
} from "../types";
import { approveRequest, declineRequest, getRequests } from "../api/requests";
import { useAuth } from "../context/AuthContext";
import { formatPrice } from "../utils/format";
import Loading from "./Loading";
import ErrorMessage from "./ErrorMessage";

interface Props {
  weddingId: string;
  onPendingCountChange?: (count: number) => void;
}

const RequestList: React.FC<Props> = ({ weddingId, onPendingCountChange }) => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<GuestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionId, setActionId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await getRequests(weddingId);
      setRequests(data);
      const pendingCount = (data || []).filter((r) => r.status === "pending").length;
      onPendingCountChange?.(pendingCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, [weddingId, onPendingCountChange]);

  useEffect(() => {
    if (user) load();
    else setLoading(false);
  }, [user, load]);

  const handleApprove = async (id: string) => {
    setActionId(id);
    try {
      await approveRequest(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Approve failed");
    } finally {
      setActionId(null);
    }
  };

  const handleDecline = async (id: string) => {
    setActionId(id);
    try {
      await declineRequest(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Decline failed");
    } finally {
      setActionId(null);
    }
  };

  if (!user) {
    return (
      <div className="form">
        <p className="form-hint">Sign in as the wedding host to manage requests.</p>
        <Link to="/login" className="btn btn-primary">
          Sign In
        </Link>
      </div>
    );
  }

  if (loading) return <Loading message="Loading requests..." />;
  if (error) return <ErrorMessage message={error} onRetry={load} />;

  const pending = requests.filter((r) => r.status === "pending");

  if (pending.length === 0) {
    return <p className="empty-state">No pending requests.</p>;
  }

  return (
    <div className="request-list">
      <h3>Public Seat Requests ({pending.length})</h3>
      <div className="guest-table-wrap">
        <table className="guest-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Instagram</th>
              <th>21+</th>
              <th>Pitch</th>
              <th>Vibe Preferences</th>
              <th>Dress Comfort</th>
              <th>Price Range</th>
              <th>Expires</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pending.map((req) => {
              const profile = req.goer_profile;
              const vibes =
                profile?.vibe_preferences
                  ?.map((v) => VIBE_TAG_LABELS[v] || v)
                  .join(", ") || "—";
              const dress =
                profile?.dress_comfort
                  ?.map((d) => DRESS_CODE_LABELS[d] || d)
                  .join(", ") || "—";
              const priceRange = profile
                ? `${formatPrice(profile.price_min_cents)}${
                    profile.price_max_cents != null
                      ? ` – ${formatPrice(profile.price_max_cents)}`
                      : "+"
                  }`
                : "—";

              return (
                <tr key={req.id}>
                  <td>{req.user_email || req.user_id}</td>
                  <td>
                    {profile?.instagram_handle
                      ? `@${profile.instagram_handle.replace(/^@/, "")}`
                      : "—"}
                  </td>
                  <td>{profile?.is_21_plus ? "Yes" : "No"}</td>
                  <td>{req.message || "—"}</td>
                  <td>{vibes}</td>
                  <td>{dress}</td>
                  <td>{priceRange}</td>
                  <td>
                    {req.expires_at
                      ? new Date(req.expires_at).toLocaleDateString()
                      : "—"}
                  </td>
                  <td>
                    <div className="request-actions">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={actionId === req.id}
                        onClick={() => handleApprove(req.id)}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={actionId === req.id}
                        onClick={() => handleDecline(req.id)}
                      >
                        Decline
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RequestList;
